import { createSelector } from "reselect";

import { createContactsRequestDto } from "app/pages/sales-intelligence/modules/company-analysis/helpers/contacts";
import { selectContactsSlice } from "app/pages/sales-intelligence/modules/company-analysis/store/selectors";
import { selectActionsPanelFilters } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import {
    selectProfileIsCreating,
    selectProfileIsUpdating,
    selectProfilesList,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

const select = createStatePropertySelector(selectContactsSlice);
// Contacts
export const selectCurrentCountriesIds = select("selectedEmployeeLocationIds");
export const selectContactsTableSearchText = select("tableSearchText");
export const selectContactsTableSortBy = select("sortBy");
export const selectContactsCurrentPage = select("tableCurrentPage");
export const selectContactsPageSize = select("tablePageSize");
export const selectContactsCorrectCurrentPage = createSelector(
    [selectContactsCurrentPage, selectActionsPanelFilters],
    (currentPage, actionsPanelFilters) => {
        const { page: actionPanelFiltersPage } = actionsPanelFilters;
        return typeof actionPanelFiltersPage === "undefined" ? currentPage : actionPanelFiltersPage;
    },
);

// Contact details
export const selectContactDetails = select("contactDetails");

// Contacts profiles
export const selectActiveProfileId = select("selectedProfileId");
export const selectActiveProfile = (state: RootState) => {
    const id = selectActiveProfileId(state);

    if (id === null) {
        return null;
    }

    const profiles = selectProfilesList(state);

    return profiles.find((p) => p.id === id) ?? null;
};
export const selectProfileModalIsOpened = select("isProfileModalOpened");
export const selectProfileForEdit = select("profileForEdit");

export const selectProfileIsBeingSubmitted = (state: RootState) => {
    const isBeingCreated = selectProfileIsCreating(state);
    const isBeingUpdated = selectProfileIsUpdating(state);

    return isBeingCreated || isBeingUpdated;
};

export const selectContactsRequestDto = createSelector(
    [
        selectContactsTableSearchText,
        selectContactsCurrentPage,
        selectContactDetails,
        selectActiveProfile,
        selectCurrentCountriesIds,
        selectActionsPanelFilters,
        selectContactsTableSortBy,
        (_: RootState, domain: string) => domain,
    ],
    (
        searchText,
        currentPage,
        contactDetails,
        selectedProfile,
        selectedCountriesIds,
        actionsPanelFilters,
        sortBy,
        domain,
    ) => {
        return createContactsRequestDto({
            searchText,
            currentPage,
            contactDetails,
            selectedProfile,
            selectedCountriesIds,
            actionsPanelFilters,
            domain,
            sortBy,
        });
    },
);

export const selectCountByContactsRequestDto = createSelector(
    [
        selectContactsTableSearchText,
        selectContactsCurrentPage,
        selectContactDetails,
        selectActiveProfile,
        selectActionsPanelFilters,
        (_: RootState, domain: string) => domain,
    ],
    (searchText, currentPage, contactDetails, selectedProfile, actionsPanelFilters, domain) => {
        return createContactsRequestDto({
            searchText,
            currentPage,
            contactDetails,
            selectedProfile,
            selectedCountriesIds: [],
            actionsPanelFilters,
            domain,
        });
    },
);
